import type { Message, ChatContext } from '../../../types/models';
import type { ChatCompletionMessageParam } from 'openai/resources/chat/completions';

function getContentPreview(content: string | null | undefined | any[]): string {
  if (!content) return '';
  if (typeof content === 'string') {
    return content.length > 50 ? content.substring(0, 50) + '...' : content;
  }
  if (Array.isArray(content)) {
    return JSON.stringify(content).substring(0, 50) + '...';
  }
  return String(content);
}

export function formatMessages(
  message: string,
  context: ChatContext,
  previousMessages: Message[],
  modelId?: string
): ChatCompletionMessageParam[] {
  const messages: ChatCompletionMessageParam[] = [];
  const isReasoner = modelId === 'deepseek-reasoner';

  // Always include base system prompt with code formatting instructions
  const baseSystemPrompt = `You must format all code examples using markdown code blocks with explicit language identifiers.

For example, Python code should be formatted as:
\`\`\`python
def example():
    print("Hello World")
\`\`\`

JavaScript code should be formatted as:
\`\`\`javascript
function example() {
    console.log("Hello World");
}
\`\`\`

Always specify the language in the code block syntax.`;

  // Combine with provided system prompt
  messages.push({
    role: 'system',
    content: context.systemPrompt ? `${baseSystemPrompt}\n\n${context.systemPrompt}` : baseSystemPrompt
  } as ChatCompletionMessageParam);

  // Add previous messages with proper alternation for reasoner
  if (isReasoner) {
    // Filter messages to ensure alternation
    const filteredMessages = previousMessages.reduce((acc: Message[], curr, i) => {
      if (i === 0 || curr.role !== acc[acc.length - 1].role) {
        acc.push(curr);
      }
      return acc;
    }, []);

    filteredMessages.forEach(msg => {
      let role = msg.role;
      if (!['system', 'user', 'assistant'].includes(role)) {
        role = 'user';
      }
      messages.push({
        role: role as 'system' | 'user' | 'assistant',
        content: msg.content
      } as ChatCompletionMessageParam);
    });
  } else {
    // Regular chat model - add all messages
    previousMessages.forEach(msg => {
      let role = msg.role;
      if (!['system', 'user', 'assistant'].includes(role)) {
        role = 'user';
      }
      messages.push({
        role: role as 'system' | 'user' | 'assistant',
        content: msg.content
      } as ChatCompletionMessageParam);
    });
  }

  // Add new message with proper alternation for reasoner
  if (isReasoner) {
    const lastMessage = messages[messages.length - 1];
    // Only add the message if it doesn't create consecutive messages of the same role
    if (!lastMessage || lastMessage.role !== 'user') {
      messages.push({
        role: 'user',
        content: message
      } as ChatCompletionMessageParam);
    }
  } else {
    messages.push({
      role: 'user',
      content: message
    } as ChatCompletionMessageParam);
  }

  // Log formatted messages for debugging
  console.log('Formatted messages for Deepseek:', {
    modelId,
    isReasoner,
    messageCount: messages.length,
    hasSystemPrompt: !!context.systemPrompt,
    messages: messages.map(m => ({
      role: m.role,
      contentPreview: getContentPreview(m.content)
    }))
  });

  return messages;
}